import { AuthService } from './authService';
import { extractApiError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

export interface StcBinTypeDto {
    id: number;
    companyNo: string;
    binTypeNo: number;
    binGrpNo?: number;
    pickPriorityNo: number;
    pickCustomOrder?: number;
    replenishMinLevel?: number;
    movementTypeNo?: number;
    putAwayCustomOrder?: number;
    replenishMaxLevel?: number;
    putAwayPriorityNo: number;
    reservationAllowed: boolean;
    negativeReservationAllowed: boolean;
    autoReplenishment: boolean;
    pickableYesNo: boolean;
    sharedBinYesNo?: boolean;
    sharedBinMaxQuantity?: number;
    replenishMaxBins?: number;
    replenishFixedBins?: boolean;
    replenishMaxBinsSource?: number;
    sharedBinPreferEmptyBin?: boolean;
    description1?: string;
    updatedBy?: string;
    lastUpdate?: string;
    createdBy?: string;
    created?: string;
}

export interface CreateStcBinTypeRequest {
    companyNo: string;
    binTypeNo: number;
    binGrpNo?: number;
    pickPriorityNo: number;
    pickCustomOrder?: number;
    replenishMinLevel?: number;
    movementTypeNo?: number;
    putAwayCustomOrder?: number;
    replenishMaxLevel?: number;
    putAwayPriorityNo: number;
    reservationAllowed: boolean;
    negativeReservationAllowed: boolean;
    autoReplenishment: boolean;
    pickableYesNo: boolean;
    sharedBinYesNo?: boolean;
    sharedBinMaxQuantity?: number;
    replenishMaxBins?: number;
    replenishFixedBins?: boolean;
    replenishMaxBinsSource?: number;
    sharedBinPreferEmptyBin?: boolean;
    description1?: string;
}

export interface UpdateStcBinTypeRequest {
    binTypeNo?: number;
    binGrpNo?: number;
    pickPriorityNo: number;
    pickCustomOrder?: number;
    replenishMinLevel?: number;
    movementTypeNo?: number;
    putAwayCustomOrder?: number;
    replenishMaxLevel?: number;
    putAwayPriorityNo: number;
    reservationAllowed: boolean;
    negativeReservationAllowed: boolean;
    autoReplenishment: boolean;
    pickableYesNo: boolean;
    sharedBinYesNo?: boolean;
    sharedBinMaxQuantity?: number;
    replenishMaxBins?: number;
    replenishFixedBins?: boolean;
    replenishMaxBinsSource?: number;
    sharedBinPreferEmptyBin?: boolean;
    description1?: string;
}

export interface BulkUpdateStcBinTypeRequest {
    companyNo: string;
    binTypeNo: number;
    binGrpNo?: number;
    pickPriorityNo: number;
    pickCustomOrder?: number;
    replenishMinLevel?: number;
    movementTypeNo?: number;
    putAwayCustomOrder?: number;
    replenishMaxLevel?: number;
    putAwayPriorityNo: number;
    reservationAllowed: boolean;
    negativeReservationAllowed: boolean;
    autoReplenishment: boolean;
    pickableYesNo: boolean;
    sharedBinYesNo?: boolean;
    sharedBinMaxQuantity?: number;
    replenishMaxBins?: number;
    replenishFixedBins?: boolean;
    replenishMaxBinsSource?: number;
    sharedBinPreferEmptyBin?: boolean;
    description1?: string;
}

export interface BulkDeleteResult {
    deletedCount: number;
    requestedCount: number;
    notFoundIds: number[];
}

class StcBinTypeService {
    private baseUrl = `${API_BASE_URL}/stc-bin-types`;

    async getByCompanyAndBinTypeNo(companyNo: string, binTypeNo: number): Promise<StcBinTypeDto | null> {
        try {
            const response = await AuthService.authenticatedGet(`${this.baseUrl}/${companyNo}/${binTypeNo}`);
            if (!response.ok) {
                if (response.status === 404) {
                    return null;
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return await response.json();
        } catch (error: any) {
            if (error.message?.includes('404')) {
                return null;
            }
            throw error;
        }
    }

    async getByCompanyNo(companyNo: string): Promise<StcBinTypeDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/company/${companyNo}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async getCurrent(): Promise<StcBinTypeDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/current`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async getAll(): Promise<StcBinTypeDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async create(request: CreateStcBinTypeRequest): Promise<StcBinTypeDto> {
        const response = await AuthService.authenticatedPost(`${this.baseUrl}`, request);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
        return await response.json();
    }

    async update(companyNo: string, binTypeNo: number, request: UpdateStcBinTypeRequest): Promise<StcBinTypeDto> {
        const response = await AuthService.authenticatedPut(`${this.baseUrl}/${companyNo}/${binTypeNo}`, request);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
        return await response.json();
    }

    async bulkUpdate(requests: BulkUpdateStcBinTypeRequest[]): Promise<StcBinTypeDto[]> {
        const response = await AuthService.authenticatedPut(`${this.baseUrl}/bulk`, requests);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
        return await response.json();
    }

    async delete(companyNo: string, binTypeNo: number): Promise<void> {
        const response = await AuthService.authenticatedDelete(`${this.baseUrl}/${companyNo}/${binTypeNo}`);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
    }

    async bulkDelete(ids: number[]): Promise<BulkDeleteResult> {
        const response = await AuthService.authenticatedDelete(`${this.baseUrl}/bulk`, ids);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
        return await response.json();
    }

    async exists(companyNo: string, binTypeNo: number): Promise<boolean> {
        try {
            const response = await AuthService.authenticatedGet(`${this.baseUrl}/${companyNo}/${binTypeNo}`);
            return response.ok;
        } catch (error: any) {
            return false;
        }
    }

    async search(companyNo: string, searchTerm: string): Promise<StcBinTypeDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/search/${companyNo}?searchTerm=${encodeURIComponent(searchTerm)}`);
        if (!response.ok) {
            const error = await response.json().catch(() => null);
            throw new Error(extractApiError(error, response, 'HTTP error!'));
        }
        return await response.json();
    }
}

export const stcBinTypeService = new StcBinTypeService();
